<!-- Ported from the Polaris kit: src/product/components/TemplatePreview.vue, by hand (only the campaigns use it).
     Changes: strings in i18n (CAMPAIGN.WHATSAPP.PREVIEW); the kit's ArtField placeholder for a picture/video header
     is a plain muted block with the kind's icon (ArtField needs the kit's art.css, outside "Tailwind only": PROVISÓRIO
     until the kit's art is in the app); the copy-code button is left out (the woot's template has no one-time code);
     the data is the woot's Meta template (`components`: HEADER, BODY, FOOTER, BUTTONS), the body already with the
     variables the person filled in. -->
<script setup>
// How the template will look when it is sent (WhatsApp): an optional header (text, picture, video or file), the
// words, a small footer and up to three buttons. A preview: the buttons are drawn and do nothing.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  Image16,
  Launch16,
  Phone16,
  Reply16,
  VideoFilled16,
} from '@carbon/icons-vue';
import FileAttachment from 'dashboard/components-next/polaris/cards/FileAttachment.vue';
import {
  Bubble,
  BubbleContent,
} from 'dashboard/components-next/polaris/ui/bubble';

const props = defineProps({
  template: { type: Object, required: true }, // { name, components: [{ type, format?, text?, buttons? }] }
  body: { type: String, default: '' }, // the body with the variables replaced
});

const { t } = useI18n();

const find = type => props.template.components?.find(c => c.type === type);
const header = computed(() => find('HEADER'));
const footer = computed(() => find('FOOTER')?.text || '');
const buttons = computed(() => find('BUTTONS')?.buttons?.slice(0, 3) ?? []);
const bodyText = computed(() => props.body || find('BODY')?.text || '');
const headerKind = computed(() => (header.value?.format || '').toUpperCase());

const buttonIcon = type =>
  ({ URL: Launch16, PHONE_NUMBER: Phone16 })[type] ?? Reply16;
</script>

<template>
  <figure class="grid max-w-xs gap-1" data-template-preview>
    <figcaption class="type-polaris-caption text-polaris-muted-foreground">
      {{ t('CAMPAIGN.WHATSAPP.PREVIEW.TITLE') }}
    </figcaption>
    <Bubble variant="outline" class="max-w-full">
      <BubbleContent class="grid gap-2 p-2">
        <p
          v-if="headerKind === 'TEXT'"
          class="type-polaris-card-title px-1 pt-1 [overflow-wrap:anywhere]"
        >
          {{ header.text }}
        </p>
        <div
          v-else-if="headerKind === 'IMAGE' || headerKind === 'VIDEO'"
          class="flex h-32 items-center justify-center rounded-polaris-md bg-polaris-muted text-polaris-muted-foreground"
        >
          <component
            :is="headerKind === 'IMAGE' ? Image16 : VideoFilled16"
            class="size-6"
            aria-hidden="true"
          />
          <span class="sr-only">
            {{
              headerKind === 'IMAGE'
                ? t('CAMPAIGN.WHATSAPP.PREVIEW.HEADER_IMAGE')
                : t('CAMPAIGN.WHATSAPP.PREVIEW.HEADER_VIDEO')
            }}
          </span>
        </div>
        <FileAttachment
          v-else-if="headerKind === 'DOCUMENT'"
          :name="t('CAMPAIGN.WHATSAPP.PREVIEW.HEADER_DOCUMENT')"
        />
        <p
          class="type-polaris-body whitespace-pre-line px-1 [overflow-wrap:anywhere]"
        >
          {{ bodyText }}
        </p>
        <p
          v-if="footer"
          class="type-polaris-caption px-1 text-polaris-muted-foreground [overflow-wrap:anywhere]"
        >
          {{ footer }}
        </p>
      </BubbleContent>
    </Bubble>
    <div v-if="buttons.length" class="grid gap-1">
      <div
        v-for="(button, at) in buttons"
        :key="at"
        class="flex min-w-0 items-center justify-center gap-2 rounded-polaris-md border border-solid border-polaris-border bg-polaris-background px-3 py-1.5 type-polaris-label text-polaris-foreground"
        data-template-button
      >
        <component
          :is="buttonIcon(button.type)"
          class="size-4 shrink-0"
          aria-hidden="true"
        />
        <span class="truncate">{{ button.text }}</span>
      </div>
    </div>
  </figure>
</template>
